import React, { useState, useCallback, createContext, useContext, useReducer } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const FilterContext = createContext(null);

const DEFAULT_FILTERS = {
  '/property-comparison-hub': {
    propertyType: [],
    priceRange: { min: '', max: '' },
    bedrooms: '',
    bathrooms: '',
    squareFeet: { min: '', max: '' },
    daysOnMarket: '',
    status: ['active', 'sold'],
  },
  '/market-trends-analytics': {
    dateRange: '12m',
    propertyType: [],
    priceSegment: '',
    neighborhood: [],
  },
  '/investment-roi-dashboard': {
    capRateMin: '',
    cashOnCashMin: '',
    propertyType: [],
    investmentRange: { min: '', max: '' },
    holdPeriod: '5',
  },
};

function filterReducer(state, action) {
  switch (action?.type) {
    case 'SET_FILTER':
      return {
        ...state,
        [action?.path]: {
          ...state?.[action?.path],
          [action?.key]: action?.value,
        },
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        [action?.path]: { ...DEFAULT_FILTERS?.[action?.path] },
      };
    case 'RESET_ALL':
      return { ...DEFAULT_FILTERS };
    default:
      return state;
  }
}

export function FilterPersistenceProvider({ children }) {
  const [filters, dispatch] = useReducer(filterReducer, DEFAULT_FILTERS);

  const setFilter = useCallback((path, key, value) => {
    dispatch({ type: 'SET_FILTER', path, key, value });
  }, []);

  const resetFilters = useCallback((path) => {
    dispatch({ type: 'RESET_FILTERS', path });
  }, []);

  const getActiveFilterCount = useCallback((path) => {
    const f = filters?.[path];
    if (!f) return 0;
    return Object.entries(f)?.reduce((count, [, val]) => {
      if (Array.isArray(val)) return count + (val?.length > 0 ? 1 : 0);
      if (typeof val === 'object' && val !== null) {
        return count + (val?.min || val?.max ? 1 : 0);
      }
      return count + (val !== '' ? 1 : 0);
    }, 0);
  }, [filters]);

  return (
    <FilterContext.Provider value={{ filters, setFilter, resetFilters, getActiveFilterCount }}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters must be used within FilterPersistenceProvider');
  return ctx;
}

export default function FilterPersistenceManager({ currentPath, className = '' }) {
  const { filters, setFilter, resetFilters, getActiveFilterCount } = useFilters();
  const [panelOpen, setPanelOpen] = useState(false);

  const activeCount = getActiveFilterCount(currentPath);
  const currentFilters = filters?.[currentPath] || {};

  const handleReset = () => {
    resetFilters(currentPath);
  };

  return (
    <div className={`relative flex items-center gap-2 ${className}`}>
      <button
        onClick={() => setPanelOpen(prev => !prev)}
        aria-label={`Filters${activeCount > 0 ? ` (${activeCount} active)` : ''}`}
        aria-expanded={panelOpen}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background
                   hover:bg-muted transition-standard text-sm font-caption font-medium text-foreground
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <Icon name="SlidersHorizontal" size={14} color="currentColor" />
        <span className="hidden sm:inline">Filters</span>
        {activeCount > 0 && (
          <span className="filter-badge">{activeCount}</span>
        )}
      </button>

      {activeCount > 0 && (
        <button
          onClick={handleReset}
          aria-label="Clear all filters"
          className="flex items-center gap-1 text-xs font-caption text-muted-foreground
                     hover:text-accent transition-standard
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          <Icon name="X" size={12} color="currentColor" />
          <span className="hidden sm:inline">Clear</span>
        </button>
      )}

      {/* Filter Panel */}
      {panelOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[1999] bg-foreground/20"
            onClick={() => setPanelOpen(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            className="fixed right-4 z-[2000] w-80 bg-card border border-border rounded-xl"
            style={{ top: '120px', boxShadow: 'var(--shadow-xl)' }}
            role="dialog"
            aria-label="Filter options"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-heading font-semibold text-base text-foreground">
                Analysis Filters
              </h3>
              <div className="flex items-center gap-2">
                {activeCount > 0 && (
                  <button
                    onClick={handleReset}
                    className="text-xs font-caption text-accent hover:underline transition-standard"
                  >
                    Reset all
                  </button>
                )}
                <button
                  onClick={() => setPanelOpen(false)}
                  className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-muted transition-standard
                             focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Close filters"
                >
                  <Icon name="X" size={14} color="currentColor" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
              {currentPath === '/property-comparison-hub' && (
                <PropertyComparisonFilters
                  filters={currentFilters}
                  onChange={(key, val) => setFilter(currentPath, key, val)}
                />
              )}
              {currentPath === '/market-trends-analytics' && (
                <MarketTrendsFilters
                  filters={currentFilters}
                  onChange={(key, val) => setFilter(currentPath, key, val)}
                />
              )}
              {currentPath === '/investment-roi-dashboard' && (
                <InvestmentFilters
                  filters={currentFilters}
                  onChange={(key, val) => setFilter(currentPath, key, val)}
                />
              )}
            </div>

            <div className="px-5 py-4 border-t border-border">
              <Button
                variant="default"
                fullWidth
                onClick={() => setPanelOpen(false)}
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FilterRow({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-caption font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
        {label}
      </label>
      {children}
    </div>
  );
}

function FilterSelect({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e?.target?.value)}
      className="w-full h-9 px-3 text-sm font-caption bg-background border border-border rounded-lg
                 text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-standard"
    >
      <option value="">{placeholder}</option>
      {options?.map(opt => (
        <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
      ))}
    </select>
  );
}

function FilterInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e?.target?.value)}
      placeholder={placeholder}
      className="w-full h-9 px-3 text-sm font-caption bg-background border border-border rounded-lg
                 text-foreground placeholder:text-muted-foreground
                 focus:outline-none focus:ring-2 focus:ring-ring transition-standard"
    />
  );
}

function PropertyComparisonFilters({ filters, onChange }) {
  return (
    <>
      <FilterRow label="Property Type">
        <FilterSelect
          value={filters?.propertyType?.[0] || ''}
          onChange={(v) => onChange('propertyType', v ? [v] : [])}
          placeholder="All types"
          options={[
            { value: 'single-family', label: 'Single Family' },
            { value: 'condo', label: 'Condo/Co-op' },
            { value: 'townhouse', label: 'Townhouse' },
            { value: 'multi-family', label: 'Multi-Family' },
          ]}
        />
      </FilterRow>
      <FilterRow label="Bedrooms">
        <FilterSelect
          value={filters?.bedrooms || ''}
          onChange={(v) => onChange('bedrooms', v)}
          placeholder="Any"
          options={[
            { value: '1', label: '1+' },
            { value: '2', label: '2+' },
            { value: '3', label: '3+' },
            { value: '4', label: '4+' },
            { value: '5', label: '5+' },
          ]}
        />
      </FilterRow>
      <FilterRow label="Days on Market">
        <FilterSelect
          value={filters?.daysOnMarket || ''}
          onChange={(v) => onChange('daysOnMarket', v)}
          placeholder="Any"
          options={[
            { value: '7', label: 'Last 7 days' },
            { value: '30', label: 'Last 30 days' },
            { value: '90', label: 'Last 90 days' },
            { value: '180', label: 'Last 6 months' },
            { value: '365', label: 'Last 12 months' },
          ]}
        />
      </FilterRow>
      <FilterRow label="Min Price">
        <FilterInput
          type="number"
          value={filters?.priceRange?.min || ''}
          onChange={(v) => onChange('priceRange', { ...filters?.priceRange, min: v })}
          placeholder="$0"
        />
      </FilterRow>
      <FilterRow label="Max Price">
        <FilterInput
          type="number"
          value={filters?.priceRange?.max || ''}
          onChange={(v) => onChange('priceRange', { ...filters?.priceRange, max: v })}
          placeholder="No limit"
        />
      </FilterRow>
    </>
  );
}

function MarketTrendsFilters({ filters, onChange }) {
  return (
    <>
      <FilterRow label="Date Range">
        <FilterSelect
          value={filters?.dateRange || '12m'}
          onChange={(v) => onChange('dateRange', v)}
          placeholder="Select range"
          options={[
            { value: '3m', label: 'Last 3 months' },
            { value: '6m', label: 'Last 6 months' },
            { value: '12m', label: 'Last 12 months' },
            { value: '24m', label: 'Last 2 years' },
            { value: '60m', label: 'Last 5 years' },
          ]}
        />
      </FilterRow>
      <FilterRow label="Property Type">
        <FilterSelect
          value={filters?.propertyType?.[0] || ''}
          onChange={(v) => onChange('propertyType', v ? [v] : [])}
          placeholder="All types"
          options={[
            { value: 'single-family', label: 'Single Family' },
            { value: 'condo', label: 'Condo/Co-op' },
            { value: 'townhouse', label: 'Townhouse' },
          ]}
        />
      </FilterRow>
      <FilterRow label="Price Segment">
        <FilterSelect
          value={filters?.priceSegment || ''}
          onChange={(v) => onChange('priceSegment', v)}
          placeholder="All segments"
          options={[
            { value: 'entry', label: 'Entry Level (<$300K)' },
            { value: 'mid', label: 'Mid Market ($300K–$700K)' },
            { value: 'upper', label: 'Upper Market ($700K–$1.5M)' },
            { value: 'luxury', label: 'Luxury (>$1.5M)' },
          ]}
        />
      </FilterRow>
    </>
  );
}

function InvestmentFilters({ filters, onChange }) {
  return (
    <>
      <FilterRow label="Min Cap Rate (%)">
        <FilterInput
          type="number"
          value={filters?.capRateMin || ''}
          onChange={(v) => onChange('capRateMin', v)}
          placeholder="e.g. 5"
        />
      </FilterRow>
      <FilterRow label="Min Cash-on-Cash (%)">
        <FilterInput
          type="number"
          value={filters?.cashOnCashMin || ''}
          onChange={(v) => onChange('cashOnCashMin', v)}
          placeholder="e.g. 8"
        />
      </FilterRow>
      <FilterRow label="Hold Period">
        <FilterSelect
          value={filters?.holdPeriod || '5'}
          onChange={(v) => onChange('holdPeriod', v)}
          placeholder="Select period"
          options={[
            { value: '1', label: '1 year' },
            { value: '3', label: '3 years' },
            { value: '5', label: '5 years' },
            { value: '10', label: '10 years' },
            { value: '20', label: '20 years' },
          ]}
        />
      </FilterRow>
      <FilterRow label="Property Type">
        <FilterSelect
          value={filters?.propertyType?.[0] || ''}
          onChange={(v) => onChange('propertyType', v ? [v] : [])}
          placeholder="All types"
          options={[
            { value: 'single-family', label: 'Single Family' },
            { value: 'multi-family', label: 'Multi-Family' },
            { value: 'commercial', label: 'Commercial' },
            { value: 'mixed-use', label: 'Mixed Use' },
          ]}
        />
      </FilterRow>
    </>
  );
}